import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { PendingRequestsService } from '@app/api/pending-requests.service';
import { HotjarService } from '@app/services/hotjar.service';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { initializePhraseAppEditor } from 'ngx-translate-phraseapp';
import { filter, map, pairwise } from 'rxjs/operators';

import { environment } from '../environments/environment';
import { MaskConfigService } from './providers/ngx-mask.provider';
import { AuthService } from './reducers/auth/auth.service';
import { CustomerIOService } from './services/customer-io.service';
import { SentryLoggingService } from './services/sentryLogging.service';
import { TrackingService } from './services/tracking.service';
import { HubSpotService } from './startup/hub-spot.service';
import { LanguageStartup } from './startup/language-startup.service';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-root',
  template: `
    <loading-bar [pendingRequestCount]="pendingRequestCount()" />
    <router-outlet />
    <router-outlet name="modal" />
    @if (hasDevToolsAvailable) {
      <app-dev-tools />
    }
  `,
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  private auth = inject(AuthService);
  private languageStartup = inject(LanguageStartup);
  //Leave this because it starts tracking pageviews
  private gtm = inject(Angulartics2GoogleTagManager);
  private router = inject(Router);
  private featureService = inject(FeatureService);
  private hubSpotService = inject(HubSpotService);
  private trackingService = inject(TrackingService);
  private sentryLoggingService = inject(SentryLoggingService);
  private maskConfigService = inject(MaskConfigService);
  private customerIoService = inject(CustomerIOService);
  private hotjarService = inject(HotjarService);
  private destroyRef = inject(DestroyRef);
  pendingRequestCount = inject(PendingRequestsService).pendingRequestsCount;

  hasDevToolsAvailable = this.featureService.isEnabled(featureFlag.TMP_DEV_TOOLS) && environment.env !== 'production';

  ngOnInit(): void {
    this.gtm.startTracking();
    this.trackingService.startTracking();
    this.customerIoService.trackPageViews();
    this.sentryLoggingService.startLogging();
    this.maskConfigService.init();

    //do a handshake to get the authenticated state
    this.languageStartup.init();
    void this.auth.load({ ignoreMetaMessage: false }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

    void this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        pairwise(),
        map(([prevEvent, currEvent]) => {
          const previousEvent = prevEvent && Object.hasOwn(prevEvent, 'url') ? (prevEvent as NavigationEnd) : null;
          const currentEvent = currEvent && Object.hasOwn(currEvent, 'url') ? (currEvent as NavigationEnd) : null;
          if (previousEvent && currentEvent) {
            // Check whether a modal is opened, then don't scroll
            const openModalCheck = !previousEvent.url.includes('modal') && currentEvent.url.includes('modal');
            // Check whether a modal is closed, then don't scroll
            const closeModalCheck = previousEvent.url.includes('modal') && !currentEvent.url.includes('modal');
            // Check whether a modal is opened after a current modal, then don't scroll
            const multipleModalCheck = previousEvent.url.includes('modal') && currentEvent.url.includes('modal');

            if (openModalCheck || closeModalCheck || multipleModalCheck) {
              return false;
            } else {
              return true;
            }
          }

          if (currentEvent) {
            if (currentEvent.url.includes('modal')) {
              return false;
            } else {
              return true;
            }
          }
          return false;
        }),
        filter((mustScrollToTop) => !!mustScrollToTop),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => window.scrollTo(0, 0));

    void this.router.events
      .pipe(
        filter((navigationEvent) => navigationEvent instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((navigationEvent: NavigationError) => {
        const error = navigationEvent.error;
        if (
          error &&
          error.message &&
          (error.message.match(/Loading chunk [0-9]* failed/) ||
            navigationEvent.error.message.includes('Failed to fetch dynamically imported module'))
        ) {
          window.location.href = navigationEvent.url;
        }
      });

    this.initializeInContextEditor();
    this.hubSpotService.loadScript();
    this.hubSpotService.initializeChatWidget(true);
    this.hotjarService.init();
  }

  private initializeInContextEditor() {
    const { phraseProjectId, phraseAccountId } = environment;

    // disable phraseapp in production regardless of other conditions
    if (!phraseProjectId || !phraseAccountId || environment.env === 'production') {
      return;
    }

    // disable phraseapp if feature is not activated
    if (!this.featureService.isEnabled(featureFlag.PHRASE_IN_CONTEXT_EDITOR)) {
      return;
    }

    initializePhraseAppEditor({
      projectId: phraseProjectId,
      accountId: phraseAccountId,
      phraseEnabled: true,
      useOldICE: false,
      prefix: '{{__',
      suffix: '__}}',
      fullReparse: true,
      autoLowercase: false,
    });
  }
}
